/**
 * Format file size in bytes to human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Supported file types
export const SUPPORTED_IMAGE_TYPES = ['.gif', '.png', '.jpg', '.jpeg', '.tif', '.tiff'];
export const SUPPORTED_VIDEO_TYPES = [
  '.avi',
  '.flv',
  '.mpg',
  '.mpe',
  '.mpeg',
  '.mpv',
  '.mp2',
  '.mp4',
  '.mov',
  '.m4p',
  '.m4v',
  '.webm',
  '.wmv',
];
export const SUPPORTED_VCARD_TYPES = ['.vcf'];

export const ALL_SUPPORTED_TYPES = [
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_VIDEO_TYPES,
  ...SUPPORTED_VCARD_TYPES,
];

// File size limits
export const MAX_IMAGE_FILE_SIZE = 20 * 1024 * 1024; // 20MB
export const MAX_VIDEO_FILE_SIZE = 600 * 1024 * 1024; // 600MB
export const MAX_VCARD_FILE_SIZE = 745 * 1024; // 745KB

/**
 * Check if a file is a video file
 */
export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}

/**
 * Check if a file type is supported
 */
export function isSupportedFiletype(file: File, supportedTypes: string[]): boolean {
  const fileName = file.name.toLowerCase();
  return supportedTypes.some((type) => fileName.endsWith(type));
}

/**
 * Check if a file is a non-transcoded file type (images or vcards)
 * These files don't need server-side processing
 */
export function isNonTranscodedFileType(file: File): boolean {
  const fileName = file.name.toLowerCase();
  return (
    SUPPORTED_IMAGE_TYPES.some((type) => fileName.endsWith(type)) ||
    SUPPORTED_VCARD_TYPES.some((type) => fileName.endsWith(type))
  );
}

/**
 * Get the maximum file size allowed for a file
 */
export function getMaxFileSize(file: File): number {
  if (isVideoFile(file)) {
    return MAX_VIDEO_FILE_SIZE;
  }
  // Images and VCards have the same limit
  return MAX_IMAGE_FILE_SIZE;
}

/**
 * Validate a file for upload
 * Returns an error message if invalid, null if valid
 */
export function validateFile(file: File): string | null {
  // Check if file type is supported
  if (!isSupportedFiletype(file, ALL_SUPPORTED_TYPES)) {
    return `'${file.name}' is an unsupported file type. Please only upload supported files. Supported types are ${ALL_SUPPORTED_TYPES.join(', ')}.`;
  }

  // Check file size
  const maxSize = getMaxFileSize(file);
  if (file.size > maxSize) {
    const fileName = file.name.toLowerCase();
    const isVCard = SUPPORTED_VCARD_TYPES.some((type) => fileName.endsWith(type));

    if (isVideoFile(file) || !isVCard) {
      // Videos and images: show size in MB
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
      return `'${file.name}' is ${sizeMB}MB and too large. Please select a file smaller than ${maxSizeMB}MB.`;
    } else {
      // VCards: show size in KB
      const sizeKB = (file.size / 1024).toFixed(2);
      const maxSizeKB = (maxSize / 1024).toFixed(0);
      return `'${file.name}' is ${sizeKB}KB and too large. Please select a file smaller than ${maxSizeKB}KB.`;
    }
  }

  return null;
}
